import { AddTreatmentResponseInfoMapper } from "@/data/mapper/treatment/add-treatment-info-mapper";
import { TreatmentPatientInfoMapper } from "@/data/mapper/treatment/treatment-patient-info-mapper";
import { TreatmentSummaryMapper } from "@/data/mapper/treatment/treatment-summary-mapper";
import { AddTreatmentRequestPayload } from "@/data/payload/treatment/add-treatment-request-payload";
import { safeMapToEntity } from "@/data/repo-Implement/safe-map-to-entity";
import { TreatmentSource } from "@/data/source/treatment-source";
import { AddTreatmentResponseEntity } from "@/domain/entity/treatment/add-treatment-response-entity";
import { TreatmentListEntity } from "@/domain/entity/treatment/treatment-list-entity";
import { TreatmentRepository } from "@/domain/repository/treatment-repository";
import { SATError, SATResult } from "@sadtx/sat-error";

export class TreatmentRepositoryImpl implements TreatmentRepository {
  constructor(private readonly treatmentSource: TreatmentSource) {}

  async fetchTreatmentList(patientUuid: string): Promise<SATResult<TreatmentListEntity, SATError>> {
    const result = await this.treatmentSource.fetchTreatmentList(patientUuid);
    if (result.isFailure) {
      return SATResult.failure(result.error);
    }

    const treatments = safeMapToEntity(
      result.value.treatments,
      TreatmentSummaryMapper.toEntityList,
      "TreatmentRepositoryImpl::fetchTreatmentList",
    );
    if (treatments.isFailure) return SATResult.failure(treatments.error);

    return SATResult.success({
      patient:
        result.value.patient == null
          ? null
          : TreatmentPatientInfoMapper.toEntity(result.value.patient),
      treatments: treatments.value,
      canAddTreatment: result.value.canAddTreatment ?? null,
    });
  }

  async addTreatment(
    payload: AddTreatmentRequestPayload,
  ): Promise<SATResult<AddTreatmentResponseEntity, SATError>> {
    const result = await this.treatmentSource.addTreatment(payload);
    if (result.isFailure) {
      return SATResult.failure(result.error);
    }

    return safeMapToEntity(
      result.value,
      AddTreatmentResponseInfoMapper.toEntity,
      "TreatmentRepositoryImpl::addTreatment",
    );
  }

  async finishTreatment(treatmentUuid: string): Promise<SATResult<void, SATError>> {
    return this.treatmentSource.finishTreatment(treatmentUuid);
  }
}
